import {isNameListColumn, splitNameList} from './attendanceNameList'
import {
  AttendanceIntent,
  AttendanceRow,
  AttendanceV2Payload,
  GroupedRow,
  getComprehensiveReportData,
  hasResultSections,
  markTypeToAttendanceMode,
} from './attendanceV2Types'

export type V2ResultColumnDef = {
  key: string
  label: string
  isStatus?: boolean
  format?: (value: unknown) => string
}

/** Columns the backend reports as a 0–100 attendance rate; rendered with a `%` suffix so a bare
 *  `88.39` never reads as a count. */
const PERCENT_COLUMN_KEYS = new Set(['presentPercent', 'attendance_percentage', 'percentage'])

function formatPercentCell(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value == null || value === '' ? '—' : String(value)
  }
  return `${Math.round(value * 10) / 10}%`
}

function decorateColumn(column: V2ResultColumnDef): V2ResultColumnDef {
  if (column.format || !PERCENT_COLUMN_KEYS.has(column.key)) return column
  return {...column, format: formatPercentCell}
}

const COLUMN_LABELS: Record<string, string> = {
  teacherName: 'Teacher',
  studentName: 'Student',
  name: 'Name',
  personName: 'Name',
  date: 'Date',
  status: 'Status',
  section: 'Section',
  className: 'Class',
  grade: 'Grade',
  gradeName: 'Grade',
  subjectName: 'Subject',
  absent: 'Absent',
  absent_count: 'Absences',
  absent_days: 'Absent days',
  absentDays: 'Absent days',
  present: 'Present',
  present_days: 'Present days',
  presentDays: 'Present days',
  late: 'Late',
  late_days: 'Late days',
  lateDays: 'Late days',
  leave: 'Leave',
  leave_days: 'Leave days',
  leaveDays: 'Leave days',
  bunk: 'Bunk',
  bunk_days: 'Bunk days',
  bunkDays: 'Bunk days',
  marked_days: 'Marked days',
  markedDays: 'Marked days',
  other: 'Other',
  presentPercent: 'Present %',
  attendance_percentage: 'Attendance',
  percentage: 'Attendance',
  student_count: 'Students',
  warningCount: 'Warnings',
  label: 'Group',
  total: 'Total',
}

const COLUMN_ORDER = [
  'teacherName',
  'studentName',
  'name',
  'personName',
  'label',
  'date',
  'className',
  'grade',
  'gradeName',
  'section',
  'subjectName',
  'status',
  'present',
  'present_days',
  'presentDays',
  'absent',
  'absent_days',
  'absentDays',
  'absent_count',
  'late',
  'late_days',
  'lateDays',
  'leave',
  'leave_days',
  'leaveDays',
  'bunk',
  'bunk_days',
  'bunkDays',
  'marked_days',
  'markedDays',
  'other',
  'student_count',
  'presentPercent',
  'attendance_percentage',
  'percentage',
  'warningCount',
  'total',
]

function humanizeColumnKey(key: string): string {
  if (COLUMN_LABELS[key]) return COLUMN_LABELS[key]
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** COLUMN_ORDER holds the legacy fixed-shape keys (`student_count`, `presentPercent`, …). The SQL
 *  agent names its own columns from the query it wrote — `Student`, `Class`, `Recent Percentage` —
 *  so none of them are listed here, and alphabetising the leftovers scattered them: a by-teacher
 *  report opened on `Class`, and `Grade` landed between the two percentages. The server orders its
 *  SELECT deliberately (the grouping dimension first), and row key order carries that through, so
 *  two unlisted keys compare equal and the stable sort leaves them exactly as the server sent them. */
function compareColumnKeys(a: string, b: string): number {
  const ai = COLUMN_ORDER.indexOf(a)
  const bi = COLUMN_ORDER.indexOf(b)
  if (ai === -1 && bi === -1) return 0
  if (ai === -1) return 1
  if (bi === -1) return -1
  return ai - bi
}

function sortColumnKeys(keys: string[]): string[] {
  return [...keys].sort(compareColumnKeys)
}

export function isEmptyCellValue(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

/** A cell that says nothing: empty, or a plain zero (`0`, `"0"`, `"0.0"`, `"0%"`). */
function isZeroCellValue(value: unknown): boolean {
  if (typeof value === 'number') return value === 0
  if (typeof value === 'string') return /^0+(\.0+)?\s*%?$/.test(value.trim())
  return false
}

/** True when at least one cell carries something other than a blank or a zero. A table whose only
 *  row reads "Students 0" is the backend saying "nothing here" in tabular form — the narrative has
 *  already said it in words, and a one-cell table of zero under it is a card with nothing on it. */
export function rowsHaveValues(rows: Record<string, unknown>[]): boolean {
  return rows.some((row) =>
    Object.values(row).some((value) => !isEmptyCellValue(value) && !isZeroCellValue(value))
  )
}

/** A cell that reports something measured — a non-zero figure, a status, a date — as opposed to
 *  one that only names who or what the row is about (`Student`, `Class`). */
function isMeasureCell(key: string, value: unknown): boolean {
  if (isEmptyCellValue(value) || isZeroCellValue(value)) return false
  if (typeof value === 'number') return true
  if (/status|date/i.test(key)) return true
  return typeof value === 'string' && !Number.isNaN(Number(value.trim().replace(/%$/, '')))
}

/**
 * Drops the rows that only restate requested items the backend had no records for.
 *
 * Asked about a student (or a whole class) on a day no attendance was marked, the SQL agent still
 * returns a row per student — `Student: "dummy student", Class: "AS (A)"` — and lists the names in
 * `stats.noDataFor`. Those rows name a student and measure nothing, yet their text cells made them
 * pass `rowsHaveValues`, so "no attendance has been recorded" was followed by a table, a
 * "no information for" notice and export buttons.
 *
 * `noDataFor` is capped by the backend (12 names for a class of 26), so matching rows to it by
 * name alone leaves the rest of the class on screen. One row naming a no-data item is enough to
 * know the table lists the requested population rather than results; every row without a measure
 * then goes, listed or not. When no row names a no-data item the table is about something else —
 * a name-only roster of who was absent in the class that does have records — and is left alone.
 */
export function dropNoDataRows(
  rows: Record<string, unknown>[],
  noDataFor?: string[] | null
): Record<string, unknown>[] {
  const names = new Set(
    (Array.isArray(noDataFor) ? noDataFor : [])
      .map((name) => (typeof name === 'string' ? name.trim().toLowerCase() : ''))
      .filter(Boolean)
  )
  if (names.size === 0) return rows

  const listsNoDataItems = rows.some((row) =>
    Object.values(row).some(
      (value) => typeof value === 'string' && names.has(value.trim().toLowerCase())
    )
  )
  if (!listsNoDataItems) return rows

  return rows.filter((row) => Object.entries(row).some(([key, value]) => isMeasureCell(key, value)))
}

export function columnHasData(key: string, rows: Record<string, unknown>[]): boolean {
  return rows.some((row) => !isEmptyCellValue(row[key]))
}

export function filterColumnsWithData(
  columns: V2ResultColumnDef[],
  rows: Record<string, unknown>[]
): V2ResultColumnDef[] {
  return columns.filter((col) => columnHasData(col.key, rows))
}

function inferColumnsFromRows(rows: Record<string, unknown>[]): V2ResultColumnDef[] {
  const keys = new Set<string>()
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      keys.add(key)
    }
  }

  return sortColumnKeys([...keys]).map((key) => ({
    key,
    label: humanizeColumnKey(key),
    isStatus: key === 'status',
  }))
}

function sortColumnDefs(columns: V2ResultColumnDef[]): V2ResultColumnDef[] {
  return columns.map(decorateColumn).sort((a, b) => compareColumnKeys(a.key, b.key))
}

function resolveResultColumns(
  intent: AttendanceIntent,
  rows: Record<string, unknown>[]
): V2ResultColumnDef[] {
  const predefined = columnsForV2Intent(intent)
  const predefinedWithData = filterColumnsWithData(predefined, rows)
  const predefinedKeys = new Set(predefinedWithData.map((col) => col.key))

  const inferredExtras = filterColumnsWithData(
    inferColumnsFromRows(rows).filter((col) => !predefinedKeys.has(col.key)),
    rows
  )

  if (predefinedWithData.length > 0) {
    return sortColumnDefs([...predefinedWithData, ...inferredExtras])
  }

  return sortColumnDefs(filterColumnsWithData(inferColumnsFromRows(rows), rows))
}

export function columnsForV2Intent(intent: AttendanceIntent): V2ResultColumnDef[] {
  switch (intent) {
    case 'individual':
      return [
        {key: 'date', label: 'Date'},
        {key: 'status', label: 'Status', isStatus: true},
        {key: 'subjectName', label: 'Subject'},
      ]
    case 'ranking':
      return [
        {key: 'teacherName', label: 'Teacher'},
        {key: 'studentName', label: 'Student'},
        {key: 'name', label: 'Name'},
        {key: 'personName', label: 'Name'},
        {key: 'className', label: 'Class'},
        {key: 'section', label: 'Section'},
        {key: 'present', label: 'Present'},
        {key: 'present_days', label: 'Present days'},
        {key: 'presentDays', label: 'Present days'},
        {key: 'absent', label: 'Absent'},
        {key: 'absent_days', label: 'Absent days'},
        {key: 'absentDays', label: 'Absent days'},
        {key: 'late', label: 'Late'},
        {key: 'late_days', label: 'Late days'},
        {key: 'lateDays', label: 'Late days'},
        {key: 'leave', label: 'Leave'},
        {key: 'leave_days', label: 'Leave days'},
        {key: 'leaveDays', label: 'Leave days'},
        {key: 'bunk', label: 'Bunk'},
        {key: 'bunk_days', label: 'Bunk days'},
        {key: 'bunkDays', label: 'Bunk days'},
        {key: 'marked_days', label: 'Marked days'},
        {key: 'markedDays', label: 'Marked days'},
        {key: 'presentPercent', label: 'Present %'},
        {key: 'warningCount', label: 'Warnings'},
      ]
    case 'roster':
      return [
        {key: 'studentName', label: 'Student'},
        {key: 'grade', label: 'Grade'},
        {key: 'gradeName', label: 'Grade'},
        {key: 'className', label: 'Class'},
        {key: 'section', label: 'Section'},
        {key: 'date', label: 'Date'},
        {key: 'status', label: 'Status', isStatus: true},
      ]
    case 'summary':
    case 'compare':
      return [
        {key: 'label', label: 'Group'},
        {key: 'present', label: 'Present'},
        {key: 'absent', label: 'Absent'},
        {key: 'presentPercent', label: 'Present %'},
      ]
    case 'pattern':
      return [
        {key: 'label', label: 'Day'},
        {key: 'present', label: 'Present'},
        {key: 'absent', label: 'Absent'},
        {key: 'presentPercent', label: 'Present %'},
      ]
    default:
      return []
  }
}

export function rowsForV2Intent(
  intent: AttendanceIntent,
  rows?: AttendanceRow[],
  grouped?: GroupedRow[],
  data?: AttendanceV2Payload['data']
): Record<string, unknown>[] {
  if (intent === 'summary' || intent === 'pattern' || intent === 'compare') {
    if (grouped?.length) return grouped as Record<string, unknown>[]
    const raw = data as Record<string, unknown> | null | undefined
    // `monthly` is the `trend` kind's series; the rest are grouped/breakdown container names used
    // across the various summary-shaped kinds.
    for (const key of ['grouped', 'groups', 'items', 'breakdown', 'monthly']) {
      const candidate = raw?.[key]
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate as Record<string, unknown>[]
      }
    }
    return []
  }

  if (rows?.length) return rows as Record<string, unknown>[]

  const raw = data as Record<string, unknown> | null | undefined
  for (const key of ['rows', 'records', 'students', 'attendance', 'entries', 'list']) {
    const candidate = raw?.[key]
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate as Record<string, unknown>[]
    }
  }

  return []
}

/** True when some structured view (table, comprehensive report card, or safety-check lists) will
 *  render this payload — i.e. when the narrative may safely drop the numbers it repeats. */
export function hasStructuredAttendanceView(payload: AttendanceV2Payload): boolean {
  if (getComprehensiveReportData(payload)) return true
  if (payload.data?.unmarkedStudents?.length || payload.data?.incompleteClasses?.length) return true
  if (hasResultSections(payload)) return true
  const {columns, rows, nameLists} = getV2ResultTableData(payload)
  if (nameLists.length > 0) return true
  // The same test the result view applies: a table of zeros is not rendered, so the narrative must
  // keep the figures it would otherwise have handed off to it.
  return columns.length > 0 && rows.length > 0 && rowsHaveValues(rows)
}

/** True when the answer carries at least one packed name list — a cell of fifty comma-joined
 *  names — that the result view draws as a list of its own. */
export function hasPackedNameLists(payload: AttendanceV2Payload): boolean {
  return getV2ResultTableData(payload).nameLists.length > 0
}

export type V2NameList = {title: string; names: string[]}

/** The first text cell of a row, to label that row's name list when the table has several rows
 *  (one packed list per class, say). */
function rowLabelOf(row: Record<string, unknown>, columns: V2ResultColumnDef[]): string | null {
  for (const col of columns) {
    const value = row[col.key]
    if (typeof value === 'string' && value.trim() && Number.isNaN(Number(value))) {
      return value.trim()
    }
  }
  return null
}

/** Lifts packed name-list columns out of the table. The SQL agent answers "which students were
 *  present" with one aggregated row — `Students: 50, Student Names: "A, B, C, …"` — which renders
 *  as a one-row table with a paragraph for a cell. The column goes, each of its cells becomes a
 *  `V2NameList` for the result view to draw as a list, and the scalar cells (Students 50, Present
 *  123, Records 435) stay as the table. */
function splitOutNameLists(
  columns: V2ResultColumnDef[],
  rows: Record<string, unknown>[]
): {columns: V2ResultColumnDef[]; rows: Record<string, unknown>[]; nameLists: V2NameList[]} {
  const listColumns = columns.filter((col) => !col.isStatus && isNameListColumn(col.key, rows))
  if (!listColumns.length) return {columns, rows, nameLists: []}

  const listKeys = new Set(listColumns.map((col) => col.key))
  const keptColumns = columns.filter((col) => !listKeys.has(col.key))
  const nameLists: V2NameList[] = []

  for (const col of listColumns) {
    for (const row of rows) {
      const names = splitNameList(row[col.key], 1)
      if (!names?.length) continue
      const rowLabel = rows.length > 1 ? rowLabelOf(row, keptColumns) : null
      nameLists.push({title: rowLabel ? `${rowLabel} · ${col.label}` : col.label, names})
    }
  }

  const keptRows = rows.map((row) => {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(row)) {
      if (!listKeys.has(key)) out[key] = value
    }
    return out
  })

  return {columns: keptColumns, rows: keptRows, nameLists}
}

/** The table an answer renders: its columns, its rows, and any packed name lists lifted out of
 *  them (see `splitOutNameLists`). `splitNameLists: false` keeps the packed cells in place, for a
 *  consumer that wants the rows exactly as the backend shaped them. */
export function getV2ResultTableData(
  payload: AttendanceV2Payload,
  options: {splitNameLists?: boolean} = {}
): {
  intent: AttendanceIntent
  columns: V2ResultColumnDef[]
  rows: Record<string, unknown>[]
  nameLists: V2NameList[]
} {
  const intent = payload.querySpec?.intent ?? 'summary'
  const rawRows = dropNoDataRows(
    rowsForV2Intent(intent, payload.data?.rows, payload.data?.grouped, payload.data),
    payload.data?.stats?.noDataFor
  )
  const rawColumns = resolveResultColumns(intent, rawRows)
  if (options.splitNameLists === false) {
    return {intent, columns: rawColumns, rows: rawRows, nameLists: []}
  }
  const {columns, rows, nameLists} = splitOutNameLists(rawColumns, rawRows)
  return {intent, columns, rows, nameLists}
}

function v2RowsToParsedContent(rows: AttendanceRow[], attendanceMode: 'day' | 'subject') {
  const hasSubject = attendanceMode === 'subject' && rows.some((r) => r.subjectName?.trim())
  const columns = hasSubject ? ['Date', 'Subject', 'Status'] : ['Date', 'Status']
  const parsedRows = rows
    .map((row) => {
      const date = String(row.date ?? '').trim()
      const status = String(row.status ?? '').trim()
      if (!date || !status) return null
      if (hasSubject) {
        return {
          Date: date,
          Subject: String(row.subjectName ?? '').trim(),
          Status: status,
        }
      }
      return {Date: date, Status: status}
    })
    .filter(Boolean) as Record<string, string>[]

  return {columns, rows: parsedRows}
}

export function v2IndividualToParsedContent(payload: AttendanceV2Payload) {
  // `markType` first: it is what the backend measured, and the synthesised querySpec the portal
  // builds for agent payloads says nothing. Without this a per-period answer keeps its Subject
  // column out of both the table and the export that reads these columns.
  const mode =
    markTypeToAttendanceMode(payload.data?.markType) ?? payload.querySpec?.attendanceMode ?? 'day'
  return v2RowsToParsedContent(payload.data?.rows ?? [], mode)
}
